import type { ReactNode } from "react";

import { toSafeCards } from "@/lib/chatWidgetUrl";

import ChatStoreCarousel from "./ChatStoreCarousel";

/**
 * THE PHASE 39 SEAM. One switch over the block kind, and the reason this file exists at all is so
 * that Phase 39 can add a store carousel and a register CTA without touching the shell around it.
 *
 * PHASE 38 SHIPS THE `text` CASE AND THE FALLBACK ONLY. `stores` and `cta` are declared, they get
 * their real full-bleed slot with its real geometry, and what renders inside that slot today is the
 * block's own `text`. That is a deliberate degrade to prose, not a placeholder: an "unsupported
 * content" box would tell a visitor about our release schedule, and prose is what the block promised
 * to be able to say about itself.
 *
 * IT NEVER THROWS, AND THE FALLBACK IS GUARANTEED PRESENT AT COMPILE TIME. Every member of the
 * envelope union carries a REQUIRED `text` field, enforced in `chat-agent/src/message/blocks.ts` by
 * the `BlockFallback` constraint and its compile gate. So a kind this client has never heard of --
 * which is exactly what a Phase 39 server talking to a cached Phase 38 bundle produces -- degrades to
 * the block's own words. Nothing in this file raises, and nothing in it ever may: a render that fails
 * inside the message list blanks the whole panel (T-38-87), which turns one unknown block into a dead
 * chat. Every arm of the switch returns an element.
 *
 * The `default` arm and the two Phase 39 arms are NOT merged, even though they render the same thing
 * today. Merging them is how Phase 39 loses the note saying which cases it owns.
 */

/**
 * The block as the panel needs it: a kind to switch on and the text every kind is required to carry.
 *
 * Deliberately NOT the chat agent's `Block` union. That type lives in a separate workspace with its
 * own build and the frontend cannot import from it; the envelope arrives here as wire JSON either
 * way. `ChatMessageBubble` narrows the wire value to this shape and hands back `null` if it cannot,
 * so a malformed envelope falls through to the turn's authoritative `content` rather than into here.
 *
 * `items` IS DELIBERATELY `unknown`, AND MUST STAY THAT WAY. It carries the raw per-block payload a
 * `stores` or `cta` block arrives with, untouched. A structured item type here would either duplicate
 * the narrowing that already lives in `toSafeCards` (`@/lib/chatWidgetUrl`) or, worse, ASSERT a shape
 * nobody actually checked — and the hand-mirror reason above is exactly why the payload arrives
 * untyped in the first place. All narrowing belongs in that pure module, which the node-environment
 * runner can test; none of it belongs in a component type.
 */
export type ChatRenderableBlock = {
  kind: string;
  text: string;
  items?: unknown;
};

/**
 * The desktop full-bleed width, and it has to be exact because Phase 39 designs its carousel
 * against this number.
 *
 * 400px panel, minus 2x1px of the card's own border = a 398px content box, minus 2x16px of the
 * message list's inline padding = 366px. (368 is the number you get by forgetting the border.)
 *
 * On mobile the panel is the whole viewport, so the same derivation is `100vw - 32px`. `min()` of the
 * two is correct on both: on the desktop card the parent content box is already 366px so the cap is
 * a no-op guard, and on a phone it resolves to the mobile value.
 */
const BLOCK_FULL_BLEED_MAX_WIDTH = "min(366px, calc(100vw - 32px))";

/**
 * A non-text block may never grow the message-list row. The desktop panel is 600px tall in total, so
 * a block that expanded to its content would push the conversation off screen (T-38-88). Taller
 * content scrolls INSIDE the slot.
 */
const BLOCK_MAX_HEIGHT_PX = 320;

/** 12px vertical, 16px inline. The one place 12 appears in this shell's spacing. */
const BUBBLE_PADDING = "px-4 py-3";

/**
 * Message text: 14px/400/1.6 in `--ys-ink`.
 *
 * 1.6 and not the generic 1.5, because IBM Plex Sans Arabic sets a tall dot-and-diacritic zone above
 * its baseline and 1.5 clips it. Visible on any bot bubble of three lines or more.
 */
const BODY_TEXT_STYLE = {
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 1.6,
  color: "var(--ys-ink)",
} as const;

type BlockProps = {
  block: ChatRenderableBlock;
  role: "user" | "assistant";
  /**
   * Rendered INSIDE the text flow, after the last glyph. This is how the streaming caret trails the
   * text it is growing: passed down by the turn rather than owned here, because the caret belongs to
   * the turn's lifecycle and not to any one block.
   */
  inlineTrailing?: ReactNode;
  /**
   * Rendered inside the same bubble element, below the text. The interrupted marker and the degrade
   * note both arrive this way, which is what keeps them in the bubble the visitor was already
   * reading instead of in a row of their own.
   */
  footer?: ReactNode;
};

/**
 * The bubble. `--ys-primary-tint` for the visitor, `--ys-bg-2` for the bot.
 *
 * The user's own words get a TINT and not the accent: the accent is the send button's, and a message
 * bubble that competes with the control that sent it has no accent left to spend.
 *
 * `whitespace-pre-wrap` because model text carries its own paragraph breaks, and `break-words`
 * because a pasted URL in a 366px column otherwise scrolls the whole panel sideways.
 *
 * THE TEXT IS RENDERED AS REACT CHILDREN, and the raw-HTML escape hatch is used nowhere in this
 * shell (T-38-86): this is attacker-influenceable text rendering inside the site's own pages, and
 * React's own escaping is the mitigation. If markdown is ever wanted it needs its own sanitisation
 * decision, and this phase deliberately does not open that door.
 */
const TextBubble = ({ block, role, inlineTrailing, footer }: BlockProps) => (
  <div
    className={`max-w-[85%] rounded-[var(--ys-r-md)] ${BUBBLE_PADDING} ${
      role === "user" ? "bg-[var(--ys-primary-tint)]" : "bg-[var(--ys-bg-2)]"
    }`}
  >
    <div className="whitespace-pre-wrap break-words" style={BODY_TEXT_STYLE}>
      {block.text}
      {inlineTrailing}
    </div>
    {footer}
  </div>
);

/**
 * The full-bleed slot a non-text block gets: it escapes the bubble and spans the list's whole content
 * width, which is the 366px derived above.
 *
 * `overscroll-behavior-x: contain` is load bearing rather than a polish detail: without it a
 * horizontal drag on a carousel chains to the page scroll behind the panel, and on the desktop card
 * the page behind is deliberately still scrollable (UA-02), so the drag would move the store page the
 * visitor is asking about.
 *
 * A horizontally scrolling block STARTS AT THE INLINE-START, which under the inherited `dir="rtl"`
 * is the right edge. Nothing is done here to achieve that: `scrollLeft: 0` in an RTL box already IS
 * the inline-start. It is written down because the tempting Phase 39 "fix" is to set `scrollLeft` on
 * mount, which would scroll a carousel to its END on the first paint.
 *
 * TWO OPTIONAL PROPS, AND NOTHING ELSE ABOUT THE SLOT CHANGES. `children` renders INSTEAD of the
 * block's text; omitting it is what keeps the `cta` arm and the zero-surviving-items case working
 * with no branch of their own. `snapX` adds the one scroll declaration a carousel needs, beside the
 * containment already there. The `maxWidth`, the `maxHeight` and the class string are untouched: the
 * 366 by 320 geometry is settled and `ChatStoreCarousel` is designed against it.
 */
type BlockSlotProps = BlockProps & {
  children?: ReactNode;
  snapX?: boolean;
};

const BlockSlot = ({ block, inlineTrailing, footer, children, snapX }: BlockSlotProps) => (
  <div
    className="w-full self-stretch overflow-x-auto overflow-y-auto rounded-[var(--ys-r-md)] border border-[var(--ys-rule)] bg-[var(--ys-bg-2)] px-4 py-3"
    style={{
      maxWidth: BLOCK_FULL_BLEED_MAX_WIDTH,
      maxHeight: BLOCK_MAX_HEIGHT_PX,
      overscrollBehaviorX: "contain",
      ...(snapX === true ? { scrollSnapType: "x mandatory" } : {}),
    }}
  >
    {children === undefined ? (
      <div className="whitespace-pre-wrap break-words" style={BODY_TEXT_STYLE}>
        {block.text}
        {inlineTrailing}
      </div>
    ) : (
      children
    )}
    {/* Still after the body in both shapes, so the streaming caret and the two footers keep riding
        on the last part of a turn rather than being dropped when a block renders children. */}
    {children === undefined ? null : inlineTrailing}
    {footer}
  </div>
);

/**
 * The switch. Three named kinds plus a default, and every arm returns something renderable.
 *
 * A block may use only the four type sizes and two weights this shell declares, and only the tokens
 * the design contract's colour section lists as passing. The two it retired as measured sub-AA (the
 * third ink step and the light orange step) appear nowhere in this phase, and the bare primary orange
 * may not be a foreground. Interactive children a Phase 39 block adds get 44px touch targets and must
 * not `autoFocus` -- a block that steals focus when it streams in takes the keyboard away from a
 * visitor mid-sentence.
 */
export default function ChatBlockRenderer(props: BlockProps) {
  switch (props.block.kind) {
    case 'text':
      return <TextBubble {...props} />;
    case 'stores': {
      // PHASE 39 FILLED THIS ARM (WEB-03).
      //
      // D-08: NATIVE CSS SCROLL SNAP AND NO NEW DEPENDENCY. RTL is the browser's own behaviour for
      // it, and it is three declarations. The vendored embla carousel in `components/ui` was
      // rejected deliberately: it has zero usages anywhere in the app, so adopting it here would be
      // a library's first outing in the hardest layout direction, inside a 366px slot it was never
      // fitted to.
      //
      // D-15: DROP THE BAD ITEM, AND WHEN NOTHING SURVIVES FALL THROUGH TO THE TEXT. The narrowing
      // below is total by construction (`@/lib/chatWidgetUrl`), and this arm keeps that promise: no
      // non-null assertion, no unguarded index, nothing here can throw and blank the panel (T-38-87,
      // T-39-32). An empty result renders the slot with no children, so the block's OWN prose
      // renders -- a real sentence the server wrote, which is this shell's honesty rule since
      // Phase 38. It is called EXACTLY ONCE in this file, on the line below and nowhere else.
      const cards = toSafeCards(props.block.items);
      if (cards.length === 0) return <BlockSlot {...props} />;
      return (
        <BlockSlot {...props} snapX>
          <ChatStoreCarousel cards={cards} />
        </BlockSlot>
      );
    }
    case 'cta':
      // PHASE 39 OWNS THIS ARM. Same slot, same fallback. A CTA block is one to three labelled
      // links, and its links need the 44px target every other control in this shell has.
      return <BlockSlot {...props} />;
    default:
      // A kind this bundle has never heard of. The guaranteed `text` field is the whole reason this
      // arm can be honest prose instead of an apology.
      return <TextBubble {...props} />;
  }
}
